import { supabase } from '../lib/supabase';

export interface CancellationResult {
  success: boolean;
  cancelledJobs: number;
  failedCancellations: number;
  errors: string[];
  details: {
    jobId: string;
    qstashMessageId: string;
    qstashSuccess: boolean;
    error?: string;
  }[];
}

export interface CancellationRequest {
  bookingId: string;
  reason: string;
  businessId: string;
}

/**
 * Cancel all scheduled automation jobs for a specific booking
 */
export async function cancelBookingAutomations(
  bookingId: string,
  reason: string,
  businessId: string
): Promise<CancellationResult> {
  try {
    console.log(`🚫 Cancelling automations for booking ${bookingId}, reason: ${reason}`);

    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    // Call the cancel-automation-jobs edge function
    const { data, error } = await supabase.functions.invoke('cancel-automation-jobs', {
      body: {
        bookingId,
        reason,
        businessId
      } as CancellationRequest
    });

    if (error) {
      console.error('❌ Edge function error:', error);
      throw new Error(`Cancellation service error: ${error.message}`);
    }

    const result = data as CancellationResult;
    
    console.log('✅ Cancellation completed:', {
      success: result.success,
      cancelled: result.cancelledJobs,
      failed: result.failedCancellations
    });

    return result;

  } catch (error) {
    console.error('❌ Failed to cancel booking automations:', error);
    
    // Return error result
    return {
      success: false,
      cancelledJobs: 0,
      failedCancellations: 0,
      errors: [error instanceof Error ? error.message : 'Unknown error'],
      details: []
    };
  }
}

/**
 * Get cancellation history for a booking (for debugging/admin purposes)
 */
export async function getBookingCancellationHistory(bookingId: string) {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase
      .from('automation_cancellations')
      .select(`
        id,
        cancellation_reason,
        qstash_success,
        qstash_error,
        created_at,
        automation_job_id
      `)
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];

  } catch (error) {
    console.error('❌ Failed to fetch cancellation history:', error);
    return [];
  }
}

/**
 * Get active automation jobs for a booking (for monitoring purposes)
 */
export async function getActiveBookingAutomations(bookingId: string) {
  try {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    const { data, error } = await supabase
      .from('automation_jobs')
      .select(`
        id,
        qstash_message_id,
        automation_id,
        scheduled_for,
        current_step,
        status,
        created_at
      `)
      .eq('booking_id', bookingId)
      .in('status', ['scheduled', 'running'])
      .order('scheduled_for', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];

  } catch (error) {
    console.error('❌ Failed to fetch active automations:', error);
    return [];
  }
}
